import React, { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';

import { pad, range, scrollParentIntoView } from '../../helpers';
import {
  type Datelike,
  formatDatelike,
  isBetweenMinutesInclusiveLeft,
  roundTime,
  setTime,
} from '../../services/DateTime';

import styles from './timeSelect.module.scss';

interface Props {
  time: Datelike;
  min?: Datelike;
  max?: Datelike;
  className?: string;

  onChange(time: Datelike): void;
}

export default function TimeSelect(props: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current !== null) {
      const child = ref.current.querySelector('.button-primary');
      scrollParentIntoView(child, ref.current, ref.current.clientHeight / 2 - child.clientHeight);
    }
  }, [ref, props.time]);

  const isEnabled = useCallback(
    (date: Datelike): boolean => {
      return isBetweenMinutesInclusiveLeft(date, props.min, props.max);
    },
    [props.min, props.max],
  );

  return (
    <div className={cn(styles.timeSelect, props.className)} ref={ref}>
      {range(0, 24 * 2).map(h => {
        const hours = Math.floor(h / 2);
        const minutes = h % 2 === 1 ? 30 : 0;
        const formatted = `${pad(hours)}:${pad(minutes)}`;
        const date = setTime(props.time, hours, minutes);

        const isActive = formatDatelike(roundTime(props.time), 'HH:mm') === formatted;

        return (
          <button
            className={cn('button button-sm', { 'button-primary': isActive })}
            disabled={!isEnabled(date)}
            key={`time-${hours}-${minutes}`}
            onClick={() => props.onChange(date)}
            type="button"
          >
            {formatted}
          </button>
        );
      })}
    </div>
  );
}
